import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatEUR, formatDate, daysUntil, API } from "../lib/api";
import Layout, { Card, Label, Badge, Spinner } from "../components/Layout";
import { HelpInline, GlobalSearch } from "../components/HelpAndSearch";
import { TrendUp, Briefcase, Clock, Target, Warning, CheckCircle, Lightning, CalendarBlank, Sparkle, Lightbulb, Globe } from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, PieChart, Pie } from "recharts";

const COLORS = { APERTA: "#10B981", CONTENDIBILE: "#F59E0B", COMPROMESSA: "#EF4444" };

const ICON_MAP = { warning: Warning, info: Sparkle, tip: Lightbulb, globe: Globe, users: Briefcase, lightning: Lightning };

export default function Dashboard() {
  const nav = useNavigate();
  const [kpis, setKpis] = useState(null);
  const [insights, setInsights] = useState([]);
  const [anomalies, setAnomalies] = useState([]);
  const [reviewStatus, setReviewStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [k, i, a, rs] = await Promise.all([
          api.get("/dashboard/kpis"),
          api.get("/ai/insights").catch(() => ({ data: { insights: [] } })),
          api.get("/analytics/price-anomalies").catch(() => ({ data: { anomalies: [] } })),
          api.get("/dashboard/tenders-review-status").catch(() => ({ data: null })),
        ]);
        setKpis(k.data);
        setInsights(i.data.insights || []);
        setAnomalies(a.data.anomalies || []);
        setReviewStatus(rs.data);
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <Layout title="Dashboard"><Spinner /></Layout>;

  const downloadIcs = () => { window.location.href = `${API}/calendar.ics`; };

  return (
    <Layout
      title="Dashboard"
      subtitle="Vista d'insieme dell'operativita gare"
      actions={
        <>
          <GlobalSearch api={api} />
          <button onClick={downloadIcs} className="btn-secondary" data-testid="btn-ics">
            <CalendarBlank size={14} className="inline mr-1.5" /> Calendario .ics
          </button>
          <HelpInline page="dashboard" />
        </>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <KpiCard icon={Briefcase} label="Gare intercettate" value={kpis.total_tenders} sub={`+${kpis.new_last_30_days} ultimi 30gg`} testid="kpi-total" />
        <KpiCard icon={Target} label="In analisi" value={kpis.in_analysis} sub={`${kpis.interesting} marcate interessanti`} testid="kpi-analysis" />
        <KpiCard icon={CheckCircle} label="Decisioni BID" value={kpis.decided_bid} sub={`${kpis.decided_no_bid} NO-BID`} testid="kpi-bid" />
        <KpiCard icon={TrendUp} label="Valore totale" value={formatEUR(kpis.total_base_value)} sub="basi d'asta aggregate" testid="kpi-value" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <Card className="lg:col-span-2">
          <Label>Volume per portale</Label>
          <div style={{ height: 260 }} data-testid="chart-portals">
            <ResponsiveContainer>
              <BarChart data={kpis.by_portal} margin={{ top: 10, right: 10, bottom: 0, left: 0 }}>
                <XAxis dataKey="portal" tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip cursor={{ fill: "#fafafa" }} contentStyle={{ fontSize: 12, border: "1px solid #e4e4e7", borderRadius: 2 }} />
                <Bar dataKey="count" fill="#002FA7" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card>
          <Label>Fattibilita</Label>
          {kpis.by_feasibility.length === 0 ? (
            <div className="text-sm text-zinc-500 mt-6 text-center">Esegui l'analisi AI sulle gare per popolare</div>
          ) : (
            <div style={{ height: 260 }} data-testid="chart-feasibility">
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={kpis.by_feasibility} dataKey="count" nameKey="label" outerRadius={90} label={(e) => e.label}>
                    {kpis.by_feasibility.map((f, i) => (
                      <Cell key={i} fill={COLORS[f.label] || "#A1A1AA"} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ fontSize: 12, border: "1px solid #e4e4e7", borderRadius: 2 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>
      </div>

      <Card data-testid="upcoming-deadlines">
        <div className="flex items-center justify-between mb-3">
          <div>
            <Label>Prossime scadenze</Label>
            <h3 className="font-display text-lg font-bold tracking-tight">Gare in chiusura</h3>
          </div>
          <button onClick={() => nav("/scouting")} className="btn-ghost text-xs" data-testid="btn-go-scouting">
            Vai allo scouting completo
          </button>
        </div>
        <div className="divide-y divide-zinc-100">
          {kpis.upcoming_deadlines.length === 0 ? (
            <div className="text-sm text-zinc-500 py-6 text-center">Nessuna scadenza imminente</div>
          ) : kpis.upcoming_deadlines.map((t) => {
            const d = daysUntil(t.deadline_at);
            const urgent = d <= 7;
            return (
              <div key={t.id} onClick={() => nav(`/tenders/${t.id}`)} className="py-3 flex items-center justify-between cursor-pointer hover:bg-zinc-50 px-2 -mx-2 rounded-sm transition-colors" data-testid={`upcoming-${t.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{t.title}</div>
                  <div className="text-xs text-zinc-500 font-mono mt-0.5">{t.station_name} • {t.portal} • {t.cig}</div>
                </div>
                <div className="flex items-center gap-3 ml-4">
                  <div className="text-right">
                    <div className="font-mono text-sm">{formatEUR(t.base_amount)}</div>
                    <div className={`text-xs flex items-center justify-end gap-1 ${urgent ? "text-red-600 font-semibold" : "text-zinc-500"}`}>
                      {urgent && <Warning weight="fill" size={12} />}
                      {d}gg • {formatDate(t.deadline_at)}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      {reviewStatus && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6" data-testid="review-status-row">
          <KpiCard icon={CheckCircle} label="In carico a me" value={reviewStatus.totals.mine} sub="gare prese in esame" testid="kpi-mine" />
          <KpiCard icon={Briefcase} label="In carico ad altri" value={reviewStatus.totals.others} sub="dal team" testid="kpi-others" />
          <KpiCard icon={Sparkle} label="Mai viste" value={reviewStatus.totals.never_viewed} sub="da nessuno" testid="kpi-never" />
          <KpiCard icon={Lightbulb} label="Nuove per me" value={reviewStatus.totals.new_for_me} sub="non ancora aperte" testid="kpi-new-me" />
        </div>
      )}

      {reviewStatus && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
          <Card data-testid="mine-card">
            <div className="flex items-center justify-between mb-3">
              <div>
                <Label>Le tue gare in esame</Label>
                <h3 className="font-display text-lg font-bold tracking-tight">In carico a te</h3>
              </div>
              <Badge kind="aperta">{reviewStatus.mine.length}</Badge>
            </div>
            {reviewStatus.mine.length === 0 ? (
              <p className="text-sm text-zinc-500 py-4">Nessuna gara presa in esame da te. Apri una gara e clicca "Prendi in esame".</p>
            ) : (
              <ul className="divide-y divide-zinc-100">
                {reviewStatus.mine.map((t) => (
                  <li key={t.id} onClick={() => nav(`/tenders/${t.id}`)} className="py-2 cursor-pointer hover:bg-zinc-50 px-2 -mx-2 rounded-sm">
                    <div className="text-sm font-medium truncate">{t.title}</div>
                    <div className="text-xs text-zinc-500 font-mono">{t.portal} • presa: {t.under_review_at ? new Date(t.under_review_at).toLocaleDateString("it-IT") : ""}</div>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card data-testid="others-card">
            <div className="flex items-center justify-between mb-3">
              <div>
                <Label>In carico al team</Label>
                <h3 className="font-display text-lg font-bold tracking-tight">Gare in esame da altri</h3>
              </div>
              <Badge kind="contendibile">{reviewStatus.in_review_by_others.length}</Badge>
            </div>
            {reviewStatus.in_review_by_others.length === 0 ? (
              <p className="text-sm text-zinc-500 py-4">Nessuna gara presa dal team.</p>
            ) : (
              <ul className="divide-y divide-zinc-100">
                {reviewStatus.in_review_by_others.map((t) => (
                  <li key={t.id} onClick={() => nav(`/tenders/${t.id}`)} className="py-2 cursor-pointer hover:bg-zinc-50 px-2 -mx-2 rounded-sm">
                    <div className="text-sm font-medium truncate">{t.title}</div>
                    <div className="text-xs text-amber-700 font-mono">→ {t.under_review_by}</div>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card data-testid="never-viewed-card" className="lg:col-span-2">
            <div className="flex items-center justify-between mb-3">
              <div>
                <Label>Gare mai aperte da nessuno</Label>
                <h3 className="font-display text-lg font-bold tracking-tight">Da assegnare</h3>
              </div>
              <Badge kind="compromessa">{reviewStatus.not_yet_viewed_by_anyone.length}</Badge>
            </div>
            {reviewStatus.not_yet_viewed_by_anyone.length === 0 ? (
              <p className="text-sm text-zinc-500 py-4">Tutte le gare sono state aperte almeno una volta.</p>
            ) : (
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-4 divide-y divide-zinc-100 md:divide-y-0">
                {reviewStatus.not_yet_viewed_by_anyone.slice(0, 8).map((t) => (
                  <li key={t.id} onClick={() => nav(`/tenders/${t.id}`)} className="py-2 cursor-pointer hover:bg-zinc-50 px-2 -mx-2 rounded-sm">
                    <div className="text-sm font-medium truncate">{t.title}</div>
                    <div className="text-xs text-zinc-500 font-mono">
                      {t.portal} • scaricata: {t.scraped_at ? new Date(t.scraped_at).toLocaleDateString("it-IT") : "—"}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      )}

      <Card className="mt-6 ai-panel">
        <div className="flex items-start gap-4">
          <Lightning weight="fill" className="text-violet-600 mt-1" size={24} />
          <div>
            <div className="label-up text-violet-700">Suggerimento AI</div>
            <h4 className="font-display text-lg font-semibold tracking-tight mb-1">Vuoi popolare la tua dashboard?</h4>
            <p className="text-sm text-zinc-600">
              Apri una gara dal modulo Scouting e premi <strong>Analizza con AI</strong> per generare summary,
              estrazione requisiti, giudizio di fattibilita e identificazione incumbent.
            </p>
          </div>
        </div>
      </Card>

      {insights.length > 0 && (
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="ai-insights">
          {insights.map((ins, i) => {
            const Icon = ICON_MAP[ins.icon] || Sparkle;
            const tone = ins.kind === "warning" ? "border-amber-300 bg-amber-50/30" : ins.kind === "tip" ? "border-violet-300 bg-violet-50/30" : "border-blue-300 bg-blue-50/30";
            return (
              <Card key={i} className={`border-l-4 ${tone}`} data-testid={`insight-${i}`}>
                <div className="flex items-start gap-3">
                  <Icon weight="duotone" size={22} className="text-klein flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-display font-bold tracking-tight">{ins.title}</div>
                    <p className="text-sm text-zinc-700 mt-1">{ins.message}</p>
                    {ins.items?.length > 0 && (
                      <ul className="text-xs text-zinc-600 mt-2 space-y-1">
                        {ins.items.map((it, j) => (
                          <li key={j} className="font-mono">
                            {it.title}{it.days != null ? ` — ${it.days}gg` : ""}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {anomalies.length > 0 && (
        <Card className="mt-6" data-testid="price-anomalies">
          <Label>Anomalie prezzi su CPV</Label>
          <p className="text-xs text-zinc-500 mt-1 mb-3">Gare con importo &gt; 2.5x media storica per il CPV. Verificare la coerenza.</p>
          <div className="space-y-2">
            {anomalies.map((a, i) => (
              <div key={i} className="flex items-center justify-between text-sm border-l-2 border-amber-500 bg-amber-50/30 px-3 py-2">
                <div>
                  <div className="font-medium">{a.title}</div>
                  <div className="text-xs text-zinc-500 font-mono">CPV {a.cpv} • media: {formatEUR(a.cpv_avg)}</div>
                </div>
                <div className="text-right">
                  <div className="font-mono font-bold">{formatEUR(a.amount)}</div>
                  <div className="text-xs text-amber-700">{a.ratio}x media</div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </Layout>
  );
}

function KpiCard({ icon: Icon, label, value, sub, testid }) {
  return (
    <Card data-testid={testid}>
      <div className="flex items-start justify-between mb-3">
        <Icon weight="duotone" size={22} className="text-klein" />
      </div>
      <div className="font-display text-3xl font-extrabold tracking-tighter">{value}</div>
      <div className="label-up mt-2">{label}</div>
      {sub && <div className="text-xs text-zinc-500 mt-1">{sub}</div>}
    </Card>
  );
}
